import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { MailIcon, LockIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
interface LoginProps {
  onSwitchToSignup: () => void;
}
export function Login({
  onSwitchToSignup
}: LoginProps) {
  const {
    login,
    loginWithSSO
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    await login(email, password);
    setIsLoading(false);
  };
  const handleSSO = async (provider: 'google' | 'apple') => {
    setIsLoading(true);
    await loginWithSSO(provider);
    setIsLoading(false);
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-3224">
      <div className="w-full max-w-md" data-id="element-3225">
        <div className="text-center mb-8" data-id="element-3226">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 font-gotham" data-id="element-3227">
            IEG app
          </h1>
          <p className="text-gray-600 font-gotham" data-id="element-3228">
            Welcome back! Sign in to continue
          </p>
        </div>

        <Card padding="lg" data-id="element-3229">
          {/* SSO Buttons */}
          <div className="space-y-3 mb-6" data-id="element-3230">
            <button onClick={() => handleSSO('google')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg font-medium font-gotham hover:bg-gray-50 transition-colors disabled:opacity-50" data-id="element-3231">
              <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-3232">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-3233" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-3234" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-3235" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-3236" />
              </svg>
              Continue with Google
            </button>

            <button onClick={() => handleSSO('apple')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white rounded-lg font-medium font-gotham hover:bg-gray-900 transition-colors disabled:opacity-50" data-id="element-3237">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-3238">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-3239" />
              </svg>
              Continue with Apple
            </button>
          </div>

          <div className="relative mb-6" data-id="element-3240">
            <div className="absolute inset-0 flex items-center" data-id="element-3241">
              <div className="w-full border-t border-gray-300" data-id="element-3242"></div>
            </div>
            <div className="relative flex justify-center text-sm" data-id="element-3243">
              <span className="px-2 bg-white text-gray-500 font-gotham" data-id="element-3244">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleLogin} className="space-y-4" data-id="element-3245">
            <Input type="email" label="Email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required data-id="element-3246" />

            <Input type="password" label="Password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required data-id="element-3247" />

            <button type="button" className="text-sm font-medium font-gotham text-gray-600 hover:text-gray-900" data-id="element-3248">
              Forgot password?
            </button>

            <Button type="submit" variant="primary" fullWidth disabled={isLoading} data-id="element-3249">
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center" data-id="element-3250">
            <p className="text-sm text-gray-600 font-gotham" data-id="element-3251">
              Don't have an account?{' '}
              <button onClick={onSwitchToSignup} className="font-semibold text-gray-900 hover:underline" data-id="element-3252">
                Sign up
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>;
}